<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Contact.translations.json";
	import addressTranslations from "../components/Address.translations.json";
	import type { ContactProps } from "./ContactProps.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import ContactCard from "../components/Contact/ContactCard.svelte";
	import OtherContactCard from "../components/Contact/OtherContactCard.svelte";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import pixelMapImg from "../assets/images/contact/pixel-map.svg";
	import { formatPhoneNumber } from "../../../core/utils/formatPhoneNumber";
	import { contactPhoneNumber } from "../../../core/schema/contactPhoneNumber.js";
	import { servicePhoneNumber } from "../../../core/schema/servicePhoneNumber.js";
	import HotlineGetInTouch from "../components/HotlineGetInTouch.svelte";
	import serviceIcon from "../../../core/assets/icons/tools.svg?raw";
	import handshakeIcon from "../../../core/assets/icons/handshake.svg?raw";
	import managementIcon from "../../../core/assets/icons/management.svg?raw";

	type $$Props = ContactProps;

	const { contacts } = $$restProps as $$Props;

	const translate = getTranslate(translations);
	const addressTranslate = getTranslate(addressTranslations);

	const MARKETING_NUMBER = "+420734657690";

	const otherContacts = [
		{
			icon: serviceIcon,
			role: translate("serviceAndSupport"),
			email: translate("serviceValue"),
			phone: servicePhoneNumber,
		},
		{
			icon: handshakeIcon,
			role: translate("sales"),
			email: translate("salesValue"),
			phone: contactPhoneNumber,
		},
		{
			icon: managementIcon,
			role: translate("management"),
			email: translate("managementValue"),
			phone: MARKETING_NUMBER,
		},
	];
</script>

<Layout>
	<div class="container">
		<section id="address-and-contact" class="scroll-mt-24">
			<div>
				<div class="mx-4 flex items-center gap-4 md:mx-16 xl:mx-32">
					<span class="bg-ltprimary-200 h-px w-8"></span>
					<span class="text-ltprimary-200 uppercase">{translate("tag")}</span>
				</div>
				<div class="text-ltsecondary mx-4 mt-16 flex flex-col gap-2 md:mx-16 md:gap-8 xl:mx-32 xl:flex-row xl:gap-32">
					<div class="mb-8 flex-1 md:mb-0 xl:max-w-[40%]">
						<h1 class="text-ltprimary p-0 text-left font-light uppercase leading-none tracking-wider">
							{addressTranslate("headquartersCompanyName")}
						</h1>
					</div>
					<div class="flex-1">
						<a href={addressTranslate("headquartersGoogleMapsLink")}>
							<address>
								<div class="lg:text-md/normal mb-0 font-light not-italic tracking-[0.02em]">
									{addressTranslate("headquartersStreet")}
								</div>
								<div class="lg:text-md/normal mb-0 font-light not-italic tracking-[0.02em]">
									{addressTranslate("headquartersCity")}
								</div>
								<div class="lg:text-md/normal mb-0 font-light not-italic tracking-[0.02em]">
									{translate("headquartersState")}
								</div>
							</address>
						</a>

						<div class="my-8 flex flex-col">
							<div class="lg:text-md/normal mb-0 font-light not-italic tracking-[0.02em]">
								{translate("cin")}
								{translate("cinValue")}
							</div>
							<div class="lg:text-md/normal mb-0 font-light not-italic tracking-[0.02em]">
								{translate("vatId")}
								{translate("vatIdValue")}
							</div>
						</div>

						<div class="flex max-w-[35rem] flex-col">
							<div class="lg:text-md/normal mb-0 font-light not-italic tracking-[0.02em]">
								{translate("companyRegisterEntry")}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>

	<!-- Contacts -->
	<section class="!mb-0">
		<div class="border-lttertiary border-y">
			<div class="mx-auto max-w-[120rem]">
				<div class="mx-10 grid grid-cols-1 items-center bg-white md:mx-16 md:grid-cols-2 lg:mx-32 xl:grid-cols-4">
					<span
						class="border-lttertiary font-bahnschrift mb-px flex flex-col border-x border-b p-8 text-center text-base/[1.6] font-normal uppercase tracking-[0.02em] text-black md:text-left xl:border-b-0"
					>
						{translate("phone")}
						<span
							class="text-ltprimary text-md xl:text-2lg/none col-span-2 font-normal lowercase tracking-[0.02em] sm:col-span-3 lg:col-span-5"
						>
							<a href="tel:{contactPhoneNumber}">
								{nbspify(formatPhoneNumber(contactPhoneNumber))}
							</a>
						</span>
					</span>

					<span
						class="border-lttertiary font-bahnschrift mb-px flex flex-col border-x border-b p-8 text-center text-base/[1.6] font-normal uppercase tracking-[0.02em] text-black md:border-x-0 md:border-r md:text-left xl:border-b-0"
					>
						{translate("email")}
						<span
							class="text-ltprimary text-md xl:text-2lg/none col-span-2 font-normal lowercase tracking-[0.02em] sm:col-span-3 lg:col-span-5"
						>
							<a href="mailto:{translate('emailValue')}">{translate("emailValue")}</a>
						</span>
					</span>

					<span
						class="border-lttertiary font-bahnschrift mb-px flex flex-col border-x border-b p-8 text-center text-base/[1.6] font-normal uppercase tracking-[0.02em] text-black md:border-x md:border-b-0 md:text-left xl:border-x-0 xl:border-r"
					>
						{translate("sales")}
						<span
							class="text-ltprimary text-md xl:text-2lg/none col-span-2 font-normal lowercase tracking-[0.02em] sm:col-span-3 lg:col-span-5"
						>
							<a href="mailto:{translate('salesValue')}">{translate("salesValue")}</a>
						</span>
					</span>

					<span
						class="border-lttertiary font-bahnschrift mb-px flex flex-col border-x p-8 text-center text-base/[1.6] font-normal uppercase tracking-[0.02em] text-black md:border-x-0 md:border-r md:text-left"
					>
						{translate("service")}
						<span
							class="text-ltprimary text-md xl:text-2lg/none col-span-2 font-normal lowercase tracking-[0.02em] sm:col-span-3 lg:col-span-5"
						>
							<a href="mailto:{translate('serviceValue')}">{translate("serviceValue")}</a>
						</span>
					</span>
				</div>
			</div>
		</div>
	</section>

	<div class="bg-lttertiary-100/70">
		<div class="mx-auto flex max-w-[120rem] flex-col px-4 pt-8 md:pt-16 lg:grid lg:grid-cols-2 lg:pt-0 xl:px-32">
			<div class="relative order-2 aspect-[3/2] w-full overflow-hidden rounded-lg lg:order-1">
				<StaticPicture
					image={pixelMapImg}
					alt=""
					height={561}
					width={1058}
					loading="eager"
					class="absolute inset-0"
					imgClass="absolute inset-0 size-full object-contain md:object-left"
				/>
			</div>
			<div class="order-1 flex flex-col justify-center lg:order-2 lg:ml-32">
				<h2
					class="text-ltprimary items-start text-center font-light uppercase tracking-wider lg:mt-0 lg:text-left lg:text-xl xl:text-2xl/none"
				>
					{translate("ourRealizations")}
				</h2>
			</div>
		</div>
	</div>

	<HotlineGetInTouch variant="bordered" />

	<!-- Contact cards -->
	<section class="!mb-0">
		<div class="border-lttertiary border-b">
			<div class="mx-auto max-w-[120rem]">
				<div class="mx-4 md:mx-16 lg:mx-32">
					<h2
						class="text-ltprimary items-start py-10 text-center font-light uppercase tracking-wider lg:mt-0 lg:text-xl xl:text-2xl/none"
					>
						{translate("ourPersonalities")}
					</h2>
					<div
						class="border-lttertiary -mb-px grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 [&>*:last-child]:!border-r lg:[&>*:nth-child(2n)]:border-r xl:[&>*:nth-child(2n)]:border-r-0 xl:[&>*:nth-child(3n)]:border-r"
					>
						{#each contacts as contact}
							<ContactCard {contact} />
						{/each}
					</div>
				</div>
			</div>
		</div>
	</section>
	<section>
		<div class="border-lttertiary border-b">
			<div class="mx-auto max-w-[120rem]">
				<div class="mx-4 md:mx-16 lg:mx-32">
					<h2
						class="text-ltprimary items-start py-10 text-center font-light uppercase tracking-wider lg:mt-0 lg:text-xl xl:text-2xl/none"
					>
						{translate("otherContacts")}
					</h2>
					<div
						class="border-lttertiary -mb-px grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 [&>*:last-child]:!border-r lg:[&>*:nth-child(2n)]:border-r xl:[&>*:nth-child(2n)]:border-r-0 xl:[&>*:nth-child(3n)]:border-r"
					>
						{#each otherContacts as otherContact}
							<OtherContactCard
								icon={otherContact.icon}
								role={otherContact.role}
								email={otherContact.email}
								phone={otherContact.phone}
							/>
						{/each}
					</div>
				</div>
			</div>
		</div>
	</section>
	<section id="contact-form" class="!mb-16 scroll-mt-24 lg:!mb-24">
		<ContactForm />
	</section>
</Layout>
